import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart, Instagram, Facebook, Twitter } from 'lucide-react';
import { useStoreInfo } from '@/lib/store';
import { motion } from 'framer-motion';

export function ModernFooter() {
  const currentYear = new Date().getFullYear();
  const { storeInfo } = useStoreInfo();

  const footerLinks = [
    { label: 'Products', href: '/products' },
    { label: 'Categories', href: '/categories' },
    { label: 'Track Order', href: '/track' },
    { label: 'Wishlist', href: '/wishlist' },
  ];

  const supportLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'Contact Us', href: '/contact' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Return Policy', href: '/return-policy' },
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-of-service' },
  ];

  return (
    <>
      {/* Desktop Footer */}
      <footer className="hidden md:block bg-gradient-to-b from-white to-brand-50 border-t-2 border-brand-100 mt-auto">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="col-span-1"
            >
              {storeInfo?.site_logo ? (
                <img 
                  src={`${import.meta.env.VITE_IMAGE_BASE_URL}/${storeInfo.site_logo}`}
                  alt={storeInfo.site_name}
                  className="h-12 w-auto mb-4 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <h3 className="text-2xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-400 mb-4">
                  {storeInfo?.site_name || 'Store'}
                </h3>
              )}
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {storeInfo?.slogan || 'Premium Hair Care Products'}
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                <motion.a 
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                  href="#" 
                  className="p-2 bg-brand-100 rounded-full text-brand-600 hover:bg-brand-600 hover:text-white transition-all"
                >
                  <Facebook className="h-4 w-4" />
                </motion.a>
                <motion.a 
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                  href="#" 
                  className="p-2 bg-brand-100 rounded-full text-brand-600 hover:bg-brand-600 hover:text-white transition-all"
                >
                  <Instagram className="h-4 w-4" />
                </motion.a>
                <motion.a 
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                  href="#" 
                  className="p-2 bg-brand-100 rounded-full text-brand-600 hover:bg-brand-600 hover:text-white transition-all"
                >
                  <Twitter className="h-4 w-4" />
                </motion.a>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="font-display font-bold text-gray-900 mb-6 text-sm uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500">
                Shop
              </h4>
              <ul className="space-y-3">
                {footerLinks.map((link, idx) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 + idx * 0.05 }}
                  >
                    <Link 
                      to={link.href} 
                      className="text-sm text-gray-600 hover:text-brand-600 transition-colors font-medium"
                    >
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="font-display font-bold text-gray-900 mb-6 text-sm uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500">
                Support
              </h4>
              <ul className="space-y-3">
                {supportLinks.slice(0, 4).map((link, idx) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 + idx * 0.05 }}
                  >
                    <Link 
                      to={link.href} 
                      className="text-sm text-gray-600 hover:text-brand-600 transition-colors font-medium"
                    >
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h4 className="font-display font-bold text-gray-900 mb-6 text-sm uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-brand-600 to-brand-500">
                Contact
              </h4>
              <ul className="space-y-4">
                {storeInfo?.phone_num && (
                  <li className="flex items-start gap-3 text-sm text-gray-600">
                    <Phone className="h-5 w-5 mt-0.5 text-brand-500 flex-shrink-0" />
                    <a href={`tel:${storeInfo.phone_num}`} className="hover:text-brand-600 transition-colors font-medium">
                      {storeInfo.phone_num}
                    </a>
                  </li>
                )}
                {storeInfo?.contact_email && (
                  <li className="flex items-start gap-3 text-sm text-gray-600">
                    <Mail className="h-5 w-5 mt-0.5 text-brand-500 flex-shrink-0" />
                    <a href={`mailto:${storeInfo.contact_email}`} className="break-all hover:text-brand-600 transition-colors font-medium">
                      {storeInfo.contact_email}
                    </a>
                  </li>
                )}
                {storeInfo?.shop_address && (
                  <li className="flex items-start gap-3 text-sm text-gray-600">
                    <MapPin className="h-5 w-5 mt-0.5 text-brand-500 flex-shrink-0" />
                    <span>{storeInfo.shop_address}</span>
                  </li>
                )}
              </ul>
            </motion.div>
          </div>

          {/* Bottom Bar */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="border-t-2 border-brand-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-6"
          >
            <p className="text-sm text-gray-600">
              © {currentYear} {storeInfo?.site_name || 'Store'}.
            </p>
            <div className="flex gap-6">
              {supportLinks.slice(4).map((link) => (
                <Link 
                  key={link.href}
                  to={link.href} 
                  className="text-sm text-gray-600 hover:text-brand-600 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </footer>


    </>
  );
}
